import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox,
  Layout, BlockStack, InlineStack, Box, Spinner, DataTable, Pagination, InlineGrid, Badge,
} from '@shopify/polaris';
import { EmailIcon } from '@shopify/polaris-icons';
import { useBackInStock } from '../../hooks/useBackInStock';
import type { BackInStockSettings } from '../../api/backInStock';
import { StatCard } from '../shared/StatCard';

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function LivePreview({ settings }: { settings: BackInStockSettings }) {
  const [subscribed, setSubscribed] = useState(false);

  return (
    <BlockStack gap="200">
      <div style={{ background: '#fff', borderRadius: 8, padding: 20, border: '1px solid #f0f0f3' }}>
        <Text as="p" variant="bodySm" tone="subdued">Wireless Earbuds Pro — product page</Text>
        <Box paddingBlockStart="150" paddingBlockEnd="200">
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 999, background: '#FBEAE9', color: '#8E1F0B', fontSize: 12, fontWeight: 600 }}>
            Sold out
          </span>
        </Box>
        <div style={{ background: '#f8f9fc', border: '1px solid #ecedf3', borderRadius: 12, padding: 16, maxWidth: 380 }}>
          {subscribed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#067647', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, flexShrink: 0, borderRadius: '50%', background: '#067647', color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
              {settings.success_message}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="email"
                placeholder="Your email address"
                disabled
                style={{ flex: 1, minWidth: 180, padding: '11px 13px', border: '1.5px solid #e2e2ea', borderRadius: 8, fontSize: 14, background: '#fff' }}
              />
              <button
                type="button"
                onClick={() => setSubscribed(true)}
                style={{
                  padding: '10px 16px', border: 'none', borderRadius: 6,
                  background: HEX_PATTERN.test(settings.button_color) ? settings.button_color : '#1d1d1d',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {settings.button_text}
              </button>
            </div>
          )}
        </div>
      </div>
      {subscribed && (
        <Box>
          <Button onClick={() => setSubscribed(false)}>Reset preview</Button>
        </Box>
      )}
    </BlockStack>
  );
}

export function BackInStockPanel() {
  const {
    settings, pendingCount, subscriptions, subsPage, subsLastPage,
    loading, saving, error, fetchSettings, save, fetchSubscriptions, setError,
  } = useBackInStock();

  const [form, setForm] = useState<BackInStockSettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); fetchSubscriptions(1); }, [fetchSettings, fetchSubscriptions]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Back-in-Stock Alerts">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Back-in-Stock Alerts">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load back-in-stock settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const colorValid = HEX_PATTERN.test(form.button_color);

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Back-in-Stock Alerts"
      subtitle="Let shoppers ask to be emailed the moment a sold-out product is available again"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. Changes appear on your storefront within about a minute.</p>
              </Banner>
            )}

            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <StatCard label="Shoppers Waiting" value={String(pendingCount)} icon={EmailIcon} iconColor="orange" />
              <StatCard
                label="Widget Status"
                value={form.enabled ? 'Enabled' : 'Disabled'}
                icon={EmailIcon}
                iconColor={form.enabled ? 'green' : 'indigo'}
              />
            </InlineGrid>

            <Card>
              <BlockStack gap="400">
                <Checkbox
                  label='Show a "Notify Me" button on sold-out product pages'
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />

                <TextField label="Button text" autoComplete="off" value={form.button_text} onChange={(v) => setForm({ ...form, button_text: v })} />
                <TextField
                  label="Confirmation message"
                  helpText="Shown to the shopper right after they submit their email"
                  autoComplete="off"
                  multiline={2}
                  value={form.success_message}
                  onChange={(v) => setForm({ ...form, success_message: v })}
                />

                <Box>
                  <Text as="p" variant="bodySm" tone="subdued">Button color</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <input
                      type="color"
                      value={colorValid ? form.button_color : '#1d1d1d'}
                      onChange={(e) => setForm({ ...form, button_color: e.target.value })}
                    />
                    <Box minWidth="100px">
                      <TextField label="" labelHidden autoComplete="off" value={form.button_color} onChange={(v) => setForm({ ...form, button_color: v })} />
                    </Box>
                  </InlineStack>
                  {!colorValid && (
                    <Text as="p" tone="critical" variant="bodySm">Color must be a 6-digit hex code, e.g. #1d1d1d.</Text>
                  )}
                </Box>

                <Box>
                  <Button variant="primary" loading={saving} disabled={!colorValid} onClick={handleSave}>Save Settings</Button>
                </Box>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Live Preview</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This is exactly what shoppers see on a sold-out product page. Click the button to preview the
                  confirmation message — no email is sent from here.
                </Text>
                <LivePreview settings={form} />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Waiting List</Text>
                {subscriptions.length === 0 ? (
                  <Text as="p" tone="subdued">No one is waiting on a restock yet.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Product', 'Email', 'Requested', 'Status']}
                    rows={subscriptions.map((s) => [
                      s.variant_title && s.variant_title !== 'Default Title' ? `${s.product_title} (${s.variant_title})` : s.product_title,
                      s.email,
                      s.created_at ? new Date(s.created_at).toLocaleDateString() : '—',
                      s.notified_at ? <Badge tone="success">Notified</Badge> : <Badge tone="attention">Waiting</Badge>,
                    ])}
                  />
                )}
                {subsLastPage > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={subsPage > 1}
                      onPrevious={() => fetchSubscriptions(subsPage - 1)}
                      hasNext={subsPage < subsLastPage}
                      onNext={() => fetchSubscriptions(subsPage + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
                <Text as="p" tone="subdued">
                  Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong>, open a product page, and use{' '}
                  <strong>Add block &rarr; Apps &rarr; Back-in-Stock Alerts</strong>. The button only appears when a
                  variant is actually sold out.
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
